import React, { useState } from 'react';
import { Chip, ListItemText, Menu, MenuItem, Stack, Typography } from '@mui/material';
import CheckIcon from '@mui/icons-material/Check';
import AddIcon from '@mui/icons-material/Add';
import { CHANNEL_LABELS, otherChannel } from './channelCopy';

const ORDER = ['support', 'widget'];

/*
 * "Used in: [✓ Support chat] [+ Web chat widget]" -- where a trained answer is used, and
 * the control to change it. A channel it's used in is a filled ✓ chip; one it isn't is a
 * faded "+" chip (deliberately NOT struck through, which reads as removed/broken).
 *
 *  - mode "toggle" (answer editor): clicking a chip turns that channel on/off via
 *    onChange(channels). With `lockCurrent` (training a new answer from an inbox) the
 *    inbox's own channel stays on; without it (editing a trained answer) either can be
 *    switched, e.g. to move an answer to the other channel. At least one stays on.
 *  - mode "menu" (Trained answers card): the other channel's chip opens Share / Copy, or
 *    Stop sharing.
 * Without edit access to both channels (`canShare`), the row is display-only and shows
 * only the channels the answer is used in.
 */
export default function ChannelChips({
  channels, channel, canShare, mode = 'menu', lockCurrent = true, disabled,
  onChange, onShare, onUnshare, onCopy,
}) {
  const [anchor, setAnchor] = useState(null);
  const other = otherChannel(channel);
  const sharedWithOther = channels.includes(other);
  const close = () => setAnchor(null);
  const isOn = (ch) => channels.includes(ch) || (mode === 'menu' && ch === channel);

  const interactiveFor = (ch) => {
    if (!canShare || disabled) return false;
    if (mode === 'menu') return ch === other;
    if (lockCurrent && ch === channel) return false;
    // Never switch off the last channel.
    return !(isOn(ch) && channels.length === 1);
  };

  const click = (ch) => (e) => {
    if (mode === 'menu') {
      setAnchor(e.currentTarget);
      return;
    }
    const next = isOn(ch) ? channels.filter((c) => c !== ch) : ORDER.filter((c) => c === ch || channels.includes(c));
    onChange(next);
  };

  const chipFor = (ch) => {
    const on = isOn(ch);
    const interactive = interactiveFor(ch);
    if (!on && !canShare) return null;
    return (
      <Chip
        key={ch}
        size="small"
        icon={on ? <CheckIcon /> : <AddIcon />}
        label={CHANNEL_LABELS[ch]}
        color={on ? 'primary' : 'default'}
        variant={on ? 'filled' : 'outlined'}
        onClick={interactive ? click(ch) : undefined}
        aria-pressed={interactive ? on : undefined}
        aria-label={interactive ? `${CHANNEL_LABELS[ch]}: ${on ? 'used' : 'not used'}` : undefined}
        sx={on ? undefined : { color: 'text.secondary', borderStyle: 'dashed' }}
      />
    );
  };

  return (
    <Stack direction="row" spacing={0.75} alignItems="center" flexWrap="wrap" useFlexGap>
      <Typography variant="caption" color="text.secondary">Used in:</Typography>
      {ORDER.map(chipFor)}
      {mode === 'menu' && (
        <Menu anchorEl={anchor} open={!!anchor} onClose={close}>
          {sharedWithOther ? (
            <MenuItem onClick={() => { close(); onUnshare(); }}>
              <ListItemText primary="Stop sharing" secondary={`Only the ${CHANNEL_LABELS[channel]} will use it`} />
            </MenuItem>
          ) : [
            <MenuItem key="share" onClick={() => { close(); onShare(); }}>
              <ListItemText primary="Share this answer" secondary="One answer for both. Edits apply to both." />
            </MenuItem>,
            <MenuItem key="copy" onClick={() => { close(); onCopy(); }}>
              <ListItemText primary="Copy this answer" secondary="A separate answer you can change on its own" />
            </MenuItem>,
          ]}
        </Menu>
      )}
    </Stack>
  );
}
